import React, { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControl, InputLabel, MenuItem, Select, Stack, TextField,
  Typography,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { toast } from "react-toastify";
import {
  getCarrierOnboardingDirectory,
  sendCarrierOnboardingInvitation,
  manageCarrierOnboardingInvitation,
  updateTenant,
} from "../api/tenantApi";

const NEW_COMPANY = "__new_company__";
const NEW_USER = "__new_user__";
const roleOptions = [
  ["company_admin", "Company Admin"],
  ["commercial_underwriter", "Commercial Underwriter"],
  ["risk_underwriter", "Risk Underwriter"],
  ["underwriter", "Underwriter"],
  ["analyst", "Analyst"],
  ["broker", "Broker"],
  ["viewer", "Viewer"],
];

const normalize = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const statusColor = (status) => status === "activated" ? "success"
  : status === "submitted" ? "primary"
    : status === "cancelled" || status === "expired" ? "default" : "warning";

function matchTenantCompany(tenant, companies) {
  const configured = tenant?.metadata?.carrierPlatformCompanyId || tenant?.carrierPlatformCompanyId;
  if (configured && companies.some((company) => company.id === configured)) return configured;
  const tenantName = normalize(tenant?.name);
  if (!tenantName) return companies[0]?.id || NEW_COMPANY;
  const exact = companies.find((company) => normalize(company.name) === tenantName);
  if (exact) return exact.id;
  const partial = companies.find((company) => {
    const companyName = normalize(company.name);
    return companyName.includes(tenantName) || tenantName.includes(companyName);
  });
  return partial?.id || companies[0]?.id || NEW_COMPANY;
}

export default function SuretyOnboardingAdminDialog({ open, onClose, tenant }) {
  const [directory, setDirectory] = useState({ companies: [], users: [], invitations: [] });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [userId, setUserId] = useState(NEW_USER);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [activationRole, setActivationRole] = useState("company_admin");
  const [managingId, setManagingId] = useState("");

  const load = async ({ preserveSelection = false } = {}) => {
    setLoading(true);
    setError("");
    try {
      const result = await getCarrierOnboardingDirectory();
      const next = {
        companies: Array.isArray(result?.companies) ? result.companies : [],
        users: Array.isArray(result?.users) ? result.users : [],
        invitations: Array.isArray(result?.invitations) ? result.invitations : [],
      };
      setDirectory(next);
      if (!preserveSelection || !companyId) setCompanyId(matchTenantCompany(tenant, next.companies));
    } catch (requestError) {
      setError(requestError?.response?.data?.error || requestError?.message || "Could not load carrier clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setUserId(NEW_USER);
    setFullName("");
    setEmail("");
    setActivationRole("company_admin");
    load();
    // `load` intentionally runs once per dialog opening; tenant changes close the dialog.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const users = useMemo(() => directory.users.filter((user) => user.company_id === companyId), [directory.users, companyId]);
  const invitations = useMemo(() => directory.invitations.filter((invite) => invite.company_id === companyId), [directory.invitations, companyId]);
  const selectedCompany = directory.companies.find((company) => company.id === companyId);

  const chooseCompany = (value) => {
    setCompanyId(value);
    setUserId(NEW_USER);
    setFullName("");
    setEmail("");
    if (value !== NEW_COMPANY) setCompanyName("");
  };

  const chooseUser = (value) => {
    setUserId(value);
    if (value === NEW_USER) {
      setFullName("");
      setEmail("");
      return;
    }
    const user = directory.users.find((candidate) => candidate.user_id === value);
    setFullName(user?.full_name || "");
    setEmail(user?.email || "");
    if (user?.roles?.includes("company_admin")) setActivationRole("company_admin");
    else if (roleOptions.some(([value]) => value === user?.roles?.[0])) setActivationRole(user.roles[0]);
    else setActivationRole("company_admin");
  };

  const canSend = companyId && (companyId !== NEW_COMPANY || companyName.trim())
    && (userId !== NEW_USER || (fullName.trim() && email.trim()));

  const send = async () => {
    if (!canSend) return;
    setBusy(true);
    try {
      const result = await sendCarrierOnboardingInvitation({
        companyId: companyId === NEW_COMPANY ? undefined : companyId,
        companyName: companyId === NEW_COMPANY ? companyName.trim() : undefined,
        userId: userId === NEW_USER ? undefined : userId,
        fullName: fullName.trim(),
        email: email.trim(),
        activationRole,
      });
      if (tenant?.id && result?.companyId && result.companyId !== (tenant.carrierPlatformCompanyId || tenant.metadata?.carrierPlatformCompanyId)) {
        try {
          await updateTenant(tenant.id, { carrierPlatformCompanyId: result.companyId });
        } catch (linkError) {
          console.warn("Carrier tenant link could not be persisted", linkError);
          toast.warning("Invitation sent, but the carrier workspace link could not be saved. Link it before managing more users.");
        }
      }
      if (result?.emailSent) toast.success(`Surety onboarding sent to ${email}`);
      else toast.warning("The onboarding invitation was created, but the email was not delivered. You can resend it below.");
      await load({ preserveSelection: companyId !== NEW_COMPANY });
      if (companyId === NEW_COMPANY && result?.companyId) setCompanyId(result.companyId);
    } catch (requestError) {
      toast.error(requestError?.response?.data?.error || requestError?.message || "Could not send onboarding");
    } finally {
      setBusy(false);
    }
  };

  const manage = async (invite, action) => {
    setManagingId(invite.id);
    try {
      const result = await manageCarrierOnboardingInvitation(invite.id, action);
      toast.success(action === "activate"
        ? "Platform access activated and password setup sent."
        : result?.emailSent ? "Onboarding invitation resent." : "Invitation refreshed, but email delivery failed.");
      await load({ preserveSelection: true });
    } catch (requestError) {
      toast.error(requestError?.response?.data?.error || requestError?.message || `Could not ${action} invitation`);
    } finally {
      setManagingId("");
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 800 }}>Surety client onboarding</DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#f8fafc" }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Select an existing carrier client and user, or create a new carrier/surety tenant. New recipients receive onboarding-only access; existing users keep their current carrier access. New platform access is activated after submission.
        </Alert>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading && !directory.companies.length ? <Box sx={{ py: 5, textAlign: "center" }}><CircularProgress size={28} /></Box> : (
          <Stack spacing={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Carrier client</InputLabel>
              <Select value={companyId} label="Carrier client" onChange={(event) => chooseCompany(event.target.value)}>
                {directory.companies.map((company) => (
                  <MenuItem key={company.id} value={company.id}>
                    {company.name}{company.company_type ? ` · ${company.company_type}` : ""}
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem value={NEW_COMPANY}>Create a new carrier/surety tenant</MenuItem>
              </Select>
            </FormControl>
            {companyId === NEW_COMPANY && (
              <TextField size="small" label="Company name" value={companyName} onChange={(event) => setCompanyName(event.target.value)} fullWidth />
            )}
            {companyId !== NEW_COMPANY && (
              <FormControl fullWidth size="small">
                <InputLabel>Onboarding recipient</InputLabel>
                <Select value={userId} label="Onboarding recipient" onChange={(event) => chooseUser(event.target.value)}>
                  <MenuItem value={NEW_USER}>Invite a new user for this carrier client</MenuItem>
                  {users.map((user) => (
                    <MenuItem key={user.user_id} value={user.user_id}>
                      {user.full_name || user.email} · {user.email}{user.roles?.length ? ` · ${user.roles.join(", ")}` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField size="small" label="Primary contact" value={fullName} onChange={(event) => setFullName(event.target.value)} disabled={userId !== NEW_USER} fullWidth />
              <TextField size="small" type="email" label="Contact email" value={email} onChange={(event) => setEmail(event.target.value)} disabled={userId !== NEW_USER} fullWidth />
            </Stack>
            <FormControl fullWidth size="small">
              <InputLabel>Platform role after activation</InputLabel>
              <Select value={activationRole} label="Platform role after activation" onChange={(event) => setActivationRole(event.target.value)}>
                {roleOptions.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </Select>
            </FormControl>
            <Button variant="contained" onClick={send} disabled={busy || loading || !canSend} startIcon={busy ? <CircularProgress size={16} /> : <MailOutlineIcon />} sx={{ alignSelf: "flex-start", textTransform: "none", fontWeight: 700 }}>
              Send onboarding
            </Button>

            {companyId !== NEW_COMPANY && (
              <Box sx={{ mt: 1 }}>
                <Divider sx={{ mb: 2 }} />
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 800 }}>Onboarding invitations</Typography>
                    <Typography variant="caption" color="text.secondary">{selectedCompany?.name || "Selected carrier client"}</Typography>
                  </Box>
                  <Button size="small" startIcon={<RefreshIcon />} onClick={() => load({ preserveSelection: true })} disabled={loading}>Refresh</Button>
                </Stack>
                {!invitations.length ? <Typography variant="body2" color="text.secondary">No onboarding invitations have been sent to this carrier client.</Typography> : invitations.map((invite) => (
                  <Box key={invite.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", p: 1.5, mb: 1, bgcolor: "#fff", border: "1px solid #e3ebf3", borderRadius: 2 }}>
                    <Box sx={{ flex: 1, minWidth: 220 }}>
                      <Typography sx={{ fontWeight: 700 }}>{invite.full_name || invite.email}</Typography>
                      <Typography variant="caption" color="text.secondary">{invite.email}</Typography>
                    </Box>
                    <Chip size="small" color={statusColor(invite.status)} label={String(invite.status || "invited").replaceAll("_", " ")} sx={{ textTransform: "capitalize" }} />
                    {!['activated', 'cancelled'].includes(invite.status) && (
                      <Button size="small" onClick={() => manage(invite, "resend")} disabled={managingId === invite.id}>Resend</Button>
                    )}
                    {invite.status === "submitted" && (
                      <Button size="small" variant="contained" startIcon={<LockOpenIcon />} onClick={() => manage(invite, "activate")} disabled={managingId === invite.id}>Activate</Button>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose} disabled={busy}>Close</Button></DialogActions>
    </Dialog>
  );
}
